import { useState } from "react";
import styled from "styled-components";
import tools from "../../../helpers/tools";
import Loader from "../Loader";

const FORMAT_DATE_COMPACT = "dd/MM/yyyy 'à' HH:mm";
const FORMAT_DATE_COURT = "dd/MM 'à' HH:mm";

// Documents d'une zone de carte (devis…), du plus récent au plus ancien, pour
// que la liste corresponde au compteur de la zone. Au-delà de deux, les plus
// anciens se déplient à la demande pour garder la carte courte (`visibles`,
// Infinity pour tout montrer, comme dans le panneau).
// `documents` : [{ id, nom, fichier, deposeLe, ia, iaLe, compagnie, ouvrir(), telecharger() }] ;
// `fichier` (nom du fichier, optionnel) sert à choisir l'icône, `compagnie`
// (optionnel) nomme la compagnie d'un devis de l'agent dans l'infobulle de la
// marque « Généré par l'IA » (PER-2500), `ouvrir` et `telecharger` rendent une
// promesse.
const DOCUMENTS_VISIBLES = 2;

const DocumentsCompacts = ({ documents = [], emptyLabel = "Aucun document.", visibles: nbVisibles = DOCUMENTS_VISIBLES }) => {
  const [tous, setTous] = useState(false);
  const tries = [...documents].sort(
    (a, b) =>
      Number(Boolean(b.enTete)) - Number(Boolean(a.enTete)) ||
      String(b.deposeLe || "").localeCompare(String(a.deposeLe || ""))
  );
  if (!tries.length) {
    return <StyledDocumentAbsent>{emptyLabel}</StyledDocumentAbsent>;
  }
  const caches = Math.max(0, tries.length - nbVisibles);
  const visibles = tous ? tries : tries.slice(0, nbVisibles);
  return (
    <StyledDocumentsCompacts>
      {visibles.map((docRef, index) => (
        <DocumentCompact key={docRef.id || index} docRef={docRef} />
      ))}
      {caches > 0 && (
        <li className="reste">
          <button type="button" className="plus" onClick={() => setTous(!tous)} aria-expanded={tous}>
            {tous ? "Masquer les plus anciens" : `Voir ${caches === 1 ? "le" : `les ${caches}`} plus ancien${caches > 1 ? "s" : ""}`}
          </button>
        </li>
      )}
    </StyledDocumentsCompacts>
  );
};

// Une ligne par document : nom cliquable pour l'aperçu, téléchargement, date
// de dépôt et marque « Généré par l'IA ». Rien en largeur fixe : le nom se
// tronque et la ligne de date se replie.
const DocumentCompact = ({ docRef }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const nom = docRef.nom || "document";
  const icone = /\.pdf$/i.test(docRef.fichier || nom) ? "fa-file-pdf" : "fa-file-image";
  const genereLe = tools.formatDate(docRef.iaLe || docRef.deposeLe, FORMAT_DATE_COMPACT);

  const telecharger = () => {
    setIsDownloading(true);
    Promise.resolve(docRef.telecharger()).finally(() => setIsDownloading(false));
  };

  const ouvrir = () => {
    setIsPreviewing(true);
    Promise.resolve(docRef.ouvrir()).finally(() => setIsPreviewing(false));
  };

  return (
    <li className="document">
      {isPreviewing ? (
        <Loader size={16} className="icone" />
      ) : (
        <i className={`fa ${icone} icone`} aria-hidden="true" />
      )}
      <button type="button" className="nom" onClick={ouvrir} disabled={isPreviewing} title={`Ouvrir ${nom} dans un nouvel onglet`}>
        {nom}
      </button>
      <button
        type="button"
        className="action"
        onClick={telecharger}
        disabled={isDownloading}
        aria-label={`Télécharger ${nom}`}
        title="Télécharger"
      >
        {isDownloading ? <Loader size={14} /> : <i className="fa fa-download" aria-hidden="true" />}
      </button>
      <span className="meta">
        {docRef.deposeLe && (
          <span title={`Déposé le ${tools.formatDate(docRef.deposeLe, FORMAT_DATE_COMPACT)}`}>
            Déposé le {tools.formatDate(docRef.deposeLe, FORMAT_DATE_COURT)}
          </span>
        )}
        {docRef.ia && (
          <span
            className="ia"
            title={`Devis créé par l'agent de tarification${docRef.compagnie ? ` chez ${docRef.compagnie}` : ""} le ${genereLe}`}
          >
            <i className="fa fa-magic" aria-hidden="true" />
            Généré par l'IA
          </span>
        )}
      </span>
    </li>
  );
};

const StyledDocumentAbsent = styled.p`
  margin: 0 0 8px;
  font-size: 13.5px;
  font-style: italic;
  color: var(--ds-slate-500);
`;

const StyledDocumentsCompacts = styled.ul`
  list-style: none;
  margin: 0 0 10px;
  padding: 0;
  display: grid;
  gap: 8px;
  font-size: 13.5px;

  > li {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    column-gap: 8px;
    align-items: center;
    min-width: 0;
  }
  // Chaque document occupe trois lignes : un cadre léger les détache.
  > li.document {
    padding: 8px;
    border: 1px solid var(--ds-slate-200);
    border-radius: 8px;
    background: var(--ds-slate-50);
  }
  > li.reste {
    padding: 0 2px;
  }
  .icone {
    width: 16px;
    font-size: 14px;
    color: var(--ds-slate-500);
  }
  .nom {
    min-width: 0;
    min-height: 24px;
    padding: 0;
    border: 0;
    border-radius: 4px;
    background: transparent;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
    font: inherit;
    font-weight: 600;
    color: var(--ds-blue);
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
    &:focus-visible {
      outline: 2px solid var(--ds-blue);
      outline-offset: 2px;
    }
    &:disabled {
      cursor: progress;
    }
  }
  .action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    margin-right: -4px;
    padding: 0;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: var(--ds-slate-500);
    font-size: 13px;
    cursor: pointer;
    transition: background 150ms, color 150ms;

    &:hover {
      background: var(--ds-slate-200);
      color: var(--ds-slate-900);
    }
    &:focus-visible {
      outline: 2px solid var(--ds-blue);
      outline-offset: 2px;
    }
  }
  .plus {
    grid-column: 1 / -1;
    justify-self: start;
    min-height: 24px;
    padding: 0;
    border: 0;
    background: transparent;
    font: inherit;
    font-size: 12.5px;
    font-weight: 600;
    text-align: left;
    color: var(--ds-blue);
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
    &:focus-visible {
      outline: 2px solid var(--ds-blue);
      outline-offset: 2px;
      border-radius: 4px;
    }
  }
  .meta {
    grid-column: 2 / -1;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 8px;
    margin-top: 2px;
    font-size: 12px;
    color: var(--ds-slate-500);
  }
  .ia {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: 20px;
    padding: 0 8px;
    border-radius: 10px;
    background: var(--ds-slate-200);
    color: var(--ds-slate-700);
    font-size: 11.5px;
    font-weight: 600;
    white-space: nowrap;
  }

  @media (max-width: 767px) {
    .nom,
    .plus {
      min-height: 44px;
    }
    .action {
      width: 44px;
      height: 44px;
    }
  }
`;

export default DocumentsCompacts;
